class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.header = document.querySelector(".header-wrapper");
    if (this.header) this.header.preventHide = false;
    this.closeTimer = 0;
    this.entryTimer = 0;
    this.handoffTimer = 0;
    this.handoffGeneration = 0;
    this.cartControl = null;
    this.cartControlHandler = null;
    this.desktopMotion = window.matchMedia("(min-width: 1100px)");
    this.mobileMotion = window.matchMedia("(max-width: 989px)");
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && !this.desktopMotion.matches && this.close()
    );
    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this, false)
    );
    this.cartRenderedHandler = () => {
      this.setHeaderCartIconAccessibility();
      const navigation = this.desktopNavigation();
      if (navigation?.dataset.panel === 'cart' && !this.cartHasItems()) {
        const keyboard = navigation.keyboard;
        navigation.closeDesktop(false, true);
        if (keyboard) navigation.querySelector('[data-nav-trigger="store"]')?.focus();
      }
      navigation?.measure();
    };
    document.addEventListener("cart:rendered", this.cartRenderedHandler);
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    if (this.relocating || this.home) return;
    this.home = document.createComment('cart-surface-home');
    this.before(this.home);
    this.modeHandler = () => this.syncSurface();
    this.desktopMotion.addEventListener('change', this.modeHandler);
    requestAnimationFrame(() => this.syncSurface());
  }

  desktopNavigation() {
    return this.desktopMotion.matches ? document.querySelector('vc-header-nav') : null;
  }

  syncSurface() {
    this.hidden = false;
    const slot = document.querySelector('[data-desktop-cart-slot]');
    const destination = this.desktopMotion.matches ? slot : this.home.parentNode;
    if (!destination) return;
    if (this.desktopMotion.matches && this.parentNode !== slot) {
      if (this.classList.contains('active')) this.close(false);
      this.relocating = true;
      slot.append(this);
      this.relocating = false;
    } else if (!this.desktopMotion.matches && this.parentNode !== this.home.parentNode) {
      this.classList.remove('active', 'vc-cart-closing');
      this.relocating = true;
      this.home.after(this);
      this.relocating = false;
    }
    this.querySelector('.drawer__inner')?.setAttribute('aria-modal', String(!this.desktopMotion.matches));
    this.setHeaderCartIconAccessibility();
  }

  disconnectedCallback() {
    if (this.relocating) return;
    document.removeEventListener("cart:rendered", this.cartRenderedHandler);
    this.desktopMotion.removeEventListener('change', this.modeHandler);
    this.cartControlAbort?.abort();
  }

  cartHasItems(cartLink = document.querySelector("#cart-icon-bubble")) {
    const count = Number.parseInt(
      cartLink?.querySelector(".header__cart-count")?.textContent || "0",
      10
    );
    return Number.isFinite(count) && count > 0;
  }

  setHeaderCartIconAccessibility() {
    this.querySelectorAll('.cart-item').forEach(item => {
      const title = item.querySelector('.cart-item__line-group');
      const remove = item.querySelector('cart-remove-button');
      if (title && remove && remove.parentElement !== title) title.append(remove);
    });
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;
    if (this.cartControl && this.cartControlHandler && this.cartControl !== cartLink) {
      this.cartControl.removeEventListener("click", this.cartControlHandler);
      this.cartControlHandler = null;
    }
    this.cartControl = cartLink;
    const hasItems = this.cartHasItems(cartLink);
    const drawerOpen = this.classList.contains("active");
    if (hasItems || drawerOpen) {
      cartLink.removeAttribute("aria-disabled");
      if (hasItems) {
        cartLink.setAttribute("href", window.routes?.cart_url || "/cart");
        cartLink.removeAttribute("tabindex");
      } else {
        cartLink.removeAttribute("href");
        cartLink.setAttribute("tabindex", "0");
      }
    } else {
      cartLink.setAttribute("aria-disabled", "true");
      cartLink.removeAttribute("href");
      cartLink.setAttribute("tabindex", "-1");
    }
    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.setAttribute("aria-controls", "CartDrawer");
    cartLink.setAttribute(
      "aria-expanded",
      String(drawerOpen)
    );
    if (this.cartControlHandler) return;
    this.cartControlHandler = (event) => {
      event.preventDefault();
      const navigation = this.desktopNavigation();
      if (navigation) {
        if (!this.cartHasItems()) return;
        navigation.keyboard = event.detail === 0;
        const initialPointerClick = event.detail > 0 && performance.now() - (this.pointerOpenedAt || 0) < 250;
        this.pointerOpenedAt = 0;
        if (navigation.desiredOpen && navigation.dataset.panel === 'cart' && !initialPointerClick) navigation.closeDesktop();
        else this.open(cartLink);
        return;
      }
      if (!this.cartHasItems(cartLink) && !this.classList.contains("active")) return;
      if (!this.classList.contains("active")) {
        const navigation = document.querySelector("vc-header-nav[data-open]");
        if (
          this.mobileMotion.matches &&
          navigation?.mobile?.matches &&
          typeof navigation.handoffToCart === "function"
        ) {
          navigation.handoffToCart(this, cartLink);
        } else {
          this.open(cartLink);
        }
      } else {
        this.close();
      }
    };
    this.cartControlAbort?.abort();
    this.cartControlAbort = new AbortController();
    const signal = this.cartControlAbort.signal;
    cartLink.addEventListener("click", this.cartControlHandler, {signal});
    cartLink.addEventListener('pointerenter', event => {
      const navigation = this.desktopNavigation();
      if (!navigation || event.pointerType !== 'mouse' || !this.cartHasItems()) return;
      clearTimeout(navigation.closeTimer);
      navigation.keyboard = false;
      if (!navigation.desiredOpen || navigation.dataset.panel !== 'cart') this.pointerOpenedAt = performance.now();
      this.open(cartLink);
    }, {signal});
    cartLink.addEventListener('pointerleave', event => {
      const navigation = this.desktopNavigation();
      if (navigation && event.pointerType === 'mouse' && !navigation.contains(event.relatedTarget)) navigation.scheduleClose();
    }, {signal});
    cartLink.addEventListener('keydown', event => {
      if (this.desktopMotion.matches && event.key === ' ') this.cartControlHandler(event);
    }, {signal});
  }

  setCartControlExpanded(expanded) {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (cartLink !== this.cartControl) this.setHeaderCartIconAccessibility();
    this.cartControl?.setAttribute("aria-expanded", String(expanded));
  }

  open(triggeredBy, options = {}) {
    this.hidden = false;
    const navigation = this.desktopNavigation();
    if (navigation) {
      if (!this.cartHasItems()) return;
      this.syncSurface();
      this.setActiveElement(triggeredBy || document.querySelector('#cart-icon-bubble'));
      navigation.openDesktop('cart');
      return;
    }
    const surfaceHandoff = Boolean(options.surfaceHandoff && this.mobileMotion.matches);
    const wasActive = this.classList.contains("active");
    const wasClosing = this.classList.contains("vc-cart-closing");
    this.handoffGeneration += 1;
    clearTimeout(this.handoffTimer);
    this.handoffTimer = 0;
    clearTimeout(this.closeTimer);
    this.closeTimer = 0;
    this.classList.remove("vc-cart-closing", "vc-cart-handoff-out");
    this.classList.toggle("vc-cart-surface-handoff", surfaceHandoff);
    this.classList.toggle("vc-cart-handoff-enter", surfaceHandoff);

    if (this.header) {
      this.header.preventHide = true;
      this.header.preventReveal = true;
    }
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const search = document.querySelector("header-search.header__search");
    if (search?.hasAttribute("open")) search.close();

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role")) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    this.classList.add("active");
    document.querySelector('vc-header-nav')?.setBrowserChromeSurface(true);
    this.setCartControlExpanded(true);

    if (this.mobileMotion.matches && (!wasActive || wasClosing)) {
      clearTimeout(this.entryTimer);
      this.classList.remove("vc-cart-enter");
      void this.offsetWidth;
      this.classList.add("vc-cart-enter");
      this.entryTimer = setTimeout(() => {
        this.classList.remove("vc-cart-enter", "vc-cart-handoff-enter", "vc-cart-surface-handoff");
        this.entryTimer = 0;
      }, this.reducedMotion.matches ? 0 : 560);
    }

    document.body.classList.add("overflow-hidden-drawer");
    // Focus must work even when motion is reduced or no transitionend fires.
    const container = this.querySelector(".drawer__inner");
    cancelAnimationFrame(this.focusFrame);
    this.focusFrame = requestAnimationFrame(() => {
      if (this.classList.contains("active")) trapFocus(container, container);
    });
  }

  close(restoreFocus = true) {
    const navigation = this.desktopNavigation();
    if (navigation) {
      if (navigation.dataset.panel === 'cart') navigation.closeDesktop(restoreFocus);
      return;
    }
    if (!this.classList.contains("active") && !this.classList.contains("vc-cart-closing")) return;
    this.setCartControlExpanded(false);
    this.handoffGeneration += 1;
    clearTimeout(this.handoffTimer);
    this.handoffTimer = 0;
    cancelAnimationFrame(this.focusFrame);
    clearTimeout(this.entryTimer);
    this.entryTimer = 0;
    this.classList.remove("vc-cart-enter", "vc-cart-handoff-enter", "vc-cart-handoff-out", "vc-cart-surface-handoff");
    clearTimeout(this.closeTimer);
    // Release the closing drawer's trap immediately so another surface (the
    // mobile navigation, for example) can take focus ownership without a
    // 380ms overlap. Return focus only after the close motion has resolved.
    removeTrapFocus();

    const finishClose = () => {
      this.closeTimer = 0;
      this.classList.remove("active", "vc-cart-closing");
      const navigation = document.querySelector('vc-header-nav');
      navigation?.setBrowserChromeSurface(navigation.hasAttribute('data-open'));
      this.setHeaderCartIconAccessibility();
      const navOwnsFocus = document.body.classList.contains("vc-nav-lock");
      if (
        restoreFocus &&
        !navOwnsFocus &&
        this.activeElement &&
        !this.activeElement.classList.contains("card-focused")
      ) {
        this.activeElement.focus();
      }
      document.body.classList.remove("overflow-hidden-drawer");

      if (this.header) {
        this.header.preventHide = false;
        this.header.preventReveal = false;
      }
    };

    if (!this.reducedMotion.matches) {
      this.classList.add("vc-cart-closing");
      this.classList.remove("active");
      this.closeTimer = setTimeout(
        finishClose,
        this.mobileMotion.matches ? 380 : 360
      );
    } else {
      finishClose();
    }
  }

  handoffToNavigation(beforeRelease) {
    if (!this.mobileMotion.matches || !this.classList.contains("active")) {
      this.close(false);
      return Promise.resolve(false);
    }

    const token = ++this.handoffGeneration;
    cancelAnimationFrame(this.focusFrame);
    clearTimeout(this.entryTimer);
    clearTimeout(this.closeTimer);
    clearTimeout(this.handoffTimer);
    this.entryTimer = 0;
    this.closeTimer = 0;
    this.classList.remove("vc-cart-enter", "vc-cart-handoff-enter", "vc-cart-closing");
    this.classList.add("vc-cart-surface-handoff", "vc-cart-handoff-out");
    removeTrapFocus();

    return new Promise((resolve) => {
      const finishHandoff = () => {
        this.handoffTimer = 0;
        if (token !== this.handoffGeneration) {
          resolve(false);
          return;
        }
        if (typeof beforeRelease === "function" && beforeRelease() === false) {
          this.classList.remove("vc-cart-handoff-out", "vc-cart-surface-handoff");
          resolve(false);
          return;
        }
        this.setCartControlExpanded(false);
        this.classList.remove(
          "active",
          "vc-cart-closing",
          "vc-cart-handoff-out",
          "vc-cart-surface-handoff"
        );
        this.hidden = true;
        this.setHeaderCartIconAccessibility();
        document.body.classList.remove("overflow-hidden-drawer");
        if (this.header) {
          this.header.preventHide = false;
          this.header.preventReveal = false;
        }
        resolve(true);
      };

      if (this.reducedMotion.matches) finishHandoff();
      else this.handoffTimer = setTimeout(finishHandoff, 160);
    });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    const shouldOpen = !this.mobileMotion.matches || this.classList.contains("active");
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    document.dispatchEvent(new CustomEvent("cart:rendered"));
    this.setHeaderCartIconAccessibility();

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay").addEventListener(
        "click",
        this.close.bind(this, false)
      );
      if (shouldOpen) this.open();
      if (
        document.querySelector("cart-popular-products-slider") &&
        typeof document.querySelector("cart-popular-products-slider").init ===
          "function"
      ) {
        document.querySelector("cart-popular-products-slider").init();
      }
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);

